import { nextTick, onBeforeUnmount } from "vue";

type TSupportedClamps = 1 | 2 | 3 | 4 | 5 | 6;
type TLineClampClass = `line-clamp-${TSupportedClamps}`;

type TOptions = {
  elementRef: Ref<HTMLElement | null>;
  isExpanded: Ref<boolean>;
  lineClamp?: TLineClampClass;
};

// Important Notes:
// Animation needs to be made on the height class, not line-clamp class
// line-clamp is just a tool to find out clamped height
// link for void el.offsetHeight: https://www.reddit.com/r/learnjavascript/comments/782qdx/what_does_void_elementoffsetwidth_do/
export const useCollapseLineClamp = ({
  elementRef,
  isExpanded,
  lineClamp = "line-clamp-5",
}: TOptions) => {
  let expandTransitionHandler: EventListener | null = null;
  let collapseTransitionHandler: EventListener | null = null;

  const createExpandTransitionHandler = (el: HTMLElement) => () => {
    el.style.height = "auto";
  };

  const createCollapseTransitionHandler = (el: HTMLElement) => () => {
    el.style.height = "auto";
  };

  onBeforeUnmount(() => {
    const el = elementRef.value;
    if (el) {
      if (expandTransitionHandler) {
        el.removeEventListener("transitionend", expandTransitionHandler);
      }
      if (collapseTransitionHandler) {
        el.removeEventListener("transitionend", collapseTransitionHandler);
      }
    }
  });
  const expand = async () => {
    const el = elementRef.value;
    if (!el) {
      return;
    }

    // Get the height of the collapsed element
    const collapsedHeight = el.offsetHeight;

    // Remove line-clamp class to get the full height of the expanded element
    el.classList.remove(lineClamp);

    // Wait for the DOM update
    await nextTick();

    // Get the full height of the expanded element
    const fullHeight = el.scrollHeight;

    // Set the height to the collapsed height before expanding, because animation can only be applied when the height is set to specific number
    el.style.height = `${collapsedHeight}px`;

    // Trigger a reflow -> browser may batch updates and not apply the transition because it doesn't know the height is changed
    void el.offsetHeight;

    // Set the transition
    el.style.transition = "height 0.25s ease";

    // Set the height to the collapsed height
    el.style.height = `${fullHeight}px`;

    isExpanded.value = true;

    // After the transition is finished, set the height to auto
    expandTransitionHandler = createExpandTransitionHandler(el);
    el.addEventListener("transitionend", expandTransitionHandler, {
      once: true,
    });
  };

  const collapse = async () => {
    const el = elementRef.value;
    if (!el) {
      return;
    }

    // Get the full height of the expanded element
    const fullHeight = el.offsetHeight;

    // Apply line-clamp class to the expanded element
    el.classList.add(lineClamp);

    // Wait for the DOM update
    await nextTick();

    // Get the height of the collapsed element
    const collapsedHeight = el.offsetHeight;

    // Set the height to the full height before collapsing, because animation can only be applied when the height is set to specific number
    el.style.height = `${fullHeight}px`;

    // Trigger a reflow -> browser may batch updates and not apply the transition because it doesn't know the height is changed
    void el.offsetHeight;

    // Set the transition
    el.style.transition = "height 0.25s ease";

    // Set the height to the collapsed height
    el.style.height = `${collapsedHeight}px`;

    isExpanded.value = false;

    // After the transition is finished, set the height to auto
    collapseTransitionHandler = createCollapseTransitionHandler(el);
    el.addEventListener("transitionend", collapseTransitionHandler, {
      once: true,
    });
  };

  const toggle = async () => {
    if (isExpanded.value) {
      await collapse();
    } else {
      await expand();
    }
  };

  return {
    expand,
    collapse,
    toggle,
  };
};
